/**
 * "Ask the real Rob", the panel's two rules (components/AskRobPanel.js):
 *
 *   - someone who said they are under 18 or still in middle or high school is
 *     pointed to a trusted adult and The Net's Student Ministry instead of
 *     being asked for a way to be reached (The Net Team spec's rule: under
 *     18, nothing stored; the server keeps none either);
 *   - when abuse came up in the conversation, the panel says the team will
 *     reach them only the way they say is safe.
 *
 * No imports, so scripts/check-contact.js runs this exact file for $0. The
 * server reads the same words by the same rule (functions/guards.js
 * saysUnder18, abuseCameUp), and the check holds the two to one table.
 */

/** Lower case, phone apostrophes made plain, runs of spaces made one (functions/guards.js normalizeForScreen). */
function normalize(text) {
  return String(text || '').toLowerCase().replace(/[‘’ʼ´`′＇‛]/g, "'").replace(/[^\S\n]+/g, ' ');
}

const AGE_UNDER_18 = String.raw`(?:1[0-7]|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen)`;
const UNDER_18 = new RegExp([
  String.raw`\b(?:i'?m|im|i\s+am)\s+(?:only\s+|just\s+)?${AGE_UNDER_18}(?:\s*(?:years?\s+old|yrs?\s+old|y\/o|yo)\b|(?=\s*(?:$|\n|[.!?,;:)]|\s+(?:and|but|so|lol|tbh|btw|now|turning|going\s+on)\b)))`,
  String.raw`\b(?:i'?m|im|i\s+am)\s+(?:a\s+|only\s+|just\s+)?${AGE_UNDER_18}[\s-]+years?[\s-]+old\b`,
  String.raw`\b(?:i'?m|im|i\s+am)\s+(?:still\s+|only\s+|just\s+)?in\s+(?:middle\s+school|junior\s+high|high\s+school|(?:the\s+)?(?:6|7|8|9|10|11|12|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth)(?:th)?\s+grade)\b(?!\s+(?:ministry|ministries|staff|teaching|coaching|administration|education|counseling))`,
  // (Not "I'm a teen pastor" or "a minor league umpire".)
  String.raw`\b(?:i'?m|im|i\s+am)\s+(?:a\s+|still\s+a\s+)?(?:high\s+schooler|middle\s+schooler|(?:freshman|sophomore|junior|senior)\s+in\s+high\s+school|(?:high\s+school|middle\s+school|hs)\s+(?:freshman|sophomore|junior|senior|student)|teen(?:ager)?|minor)\b(?!\s+(?:pastor|minister|ministry|leader|league|leaguer|group|volunteer|director|coach|mentor|counsell?or|teacher|librarian|worker|sponsor))`,
  // "14 years old here", "Age 15.", "15 f here", "I just turned 13", "I'm
  // turning 16 next week", "I'm a 7th grader", "I'm a sophomore at Grissom
  // High", "I'm a sophomore", "me and my friends are 14", "I'm a kid", "I'm
  // still in school, 10th grade". (Not "I'm a freshman in college", "a junior
  // partner", "a child of God", "my son just turned 13".)
  String.raw`(?:^|\n)[^\S\n]*(?:age\s*[:-]?\s*)?${AGE_UNDER_18}\s*(?:years?\s+old|yrs?\s+old|y\/o|yo)\b|(?:^|\n)[^\S\n]*age\s*[:-]?\s*${AGE_UNDER_18}\b|(?:^|\n|\()[^\S\n]*${AGE_UNDER_18}\s*(?:[fm]|female|male|girl|boy)\b`,
  String.raw`\b(?:i|i'?ve)\s+just\s+turned\s+${AGE_UNDER_18}\b|\b(?:i'?m|im|i\s+am)\s+turning\s+(?:1[0-8]|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen)\b`,
  String.raw`\b(?:i'?m|im|i\s+am)\s+(?:a|an)\s+(?:6|7|8|9|10|11|12|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth)(?:th)?[\s-]*grader\b|\b(?:i'?m|im|i\s+am)\s+still\s+in\s+school\b[^.!?\n]{0,15}?\b(?:6|7|8|9|10|11|12|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth)(?:th)?\s+grade\b`,
  String.raw`\b(?:i'?m|im|i\s+am)\s+(?:a|an)\s+(?:freshman|sophomore|junior|senior)\s+at\s+[\w'.-]+(?:\s+[\w'.-]+){0,3}?\s+(?:high|middle|junior\s+high|hs|ms|academy|prep)\b|\b(?:i'?m|im|i\s+am)\s+(?:a|an)\s+(?:freshman|sophomore|junior)(?=\s*(?:$|\n|[.!?,;:)]|\s+(?:and|but|so|this\s+year|now)\b))`,
  String.raw`\b(?:me\s+and\s+my\s+\w+|my\s+\w+\s+and\s+(?:i|me))\s+(?:are|'re)\s+(?:both\s+|all\s+)?${AGE_UNDER_18}(?:\s*(?:years?\s+old|yrs?\s+old|yo)\b|(?=\s*(?:$|\n|[.!?,;:)])))|\bwe(?:'re|\s+are)\s+(?:both\s+|all\s+)?${AGE_UNDER_18}(?:\s*(?:years?\s+old|yrs?\s+old|yo)\b|(?=\s*(?:$|\n|[.!?,;:)])))`,
  String.raw`\b(?:i'?m|im|i\s+am)\s+(?:just\s+|only\s+|still\s+)?(?:a\s+)?(?:kid|child|little\s+kid)\b(?!\s+(?:at\s+heart|of\s+(?:god|the\s+king|the\s+most\s+high|the\s+light|light|grace)|in\s+(?:christ|god'?s\s+family)))`,
].map((part) => `(?:${part})`).join('|'), 'i');

// The abuse hotlines, however they are written (functions/guards.js ABUSE_HOTLINE).
// The character before is matched, not looked behind at: Safari before 16.4
// refuses a whole script with a lookbehind in it (scripts/check-scripture.js).
const ABUSE_HOTLINE = /(?:^|\D)(?:1[-.\s]?)?\(?800\)?[-.\s]?(?:799[-.\s]?(?:7233|safe)|422[-.\s]?4453|4[-.\s]?a[-.\s]?child)\b/i;

/** Whether one message says they are under 18 or still in middle or high school. */
export function saysUnder18(text) {
  return typeof text === 'string' && UNDER_18.test(normalize(text));
}

/** Whether anything they said in this conversation does. */
export function underEighteenIn(messages) {
  return Array.isArray(messages) && messages.some((m) => m && m.role === 'user' && saysUnder18(m.content));
}

/**
 * Whether abuse came up here: the abuse card showed (the server's screen), or
 * an answer gave an abuse hotline (the model heard it when the screen did not).
 */
export function abuseCameUpIn(messages) {
  return Array.isArray(messages) && messages.some((m) => m && (
    m.safetyCard === 'abuse'
    || (m.role === 'assistant' && !m.local && typeof m.content === 'string' && ABUSE_HOTLINE.test(normalize(m.content)))
  ));
}
